import { useMemo } from 'react'
import { ArrowUpRight } from 'lucide-react'
import SectionHeading from '@/components/feature/SectionHeading'
import Card from '@/components/base/Card'
import useCommunity, { type CommunityLeader } from '@/hooks/useCommunity'
import { useLanguage } from '@/localization'

/** Hide a broken avatar image so the initials fallback underneath shows. */
function handleAvatarError(e: React.SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.style.display = 'none'
}

export function TeamCard({ member }: { member: CommunityLeader }) {
  const specialties = member.specialties ?? []
  const { t } = useLanguage()
  return (
    <Card variant="grass" className="flex h-full flex-col items-center text-center">
      <div className="relative mb-6">
        {/* Initials sit underneath; the avatar covers them and is hidden onError. */}
        <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-grass-dark bg-grass-dark/15 shadow-grass-glow">
          <span className="font-pixel text-lg text-grass-deep">
            {member.initials ?? member.name.slice(0, 2).toUpperCase()}
          </span>
          {member.avatar && (
            <img
              src={member.avatar}
              alt={member.name}
              onError={handleAvatarError}
              className="absolute inset-0 h-full w-full rounded-full object-cover"
              loading="lazy"
            />
          )}
        </div>
        {member.badge && (
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-grass px-3 py-1 text-sm font-bold text-white shadow-block">
            {t(member.badge)}
          </span>
        )}
      </div>

      <h3 className="text-2xl font-extrabold text-slate-deep">{member.name}</h3>
      <p className="mb-4 font-bold text-grass-deep">{t(member.role)}</p>

      {member.description && (
        <p className="mb-5 flex-1 leading-relaxed text-slate-deep/75">{t(member.description)}</p>
      )}

      {specialties.length > 0 && (
        <div className="mt-auto w-full">
          <h4 className="mb-3 font-extrabold text-slate-deep">{t('Specialties')}</h4>
          <div className="flex flex-wrap justify-center gap-2">
            {specialties.map((s) => (
              <span
                key={s}
                className="rounded-full border border-grass-dark/30 bg-grass/15 px-3 py-1 text-sm font-bold text-grass-deep"
              >
                {t(s)}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * Core team for the MC home — the shared cross-game rich person card, grass
 * skinned. Renders only `core`-tier members whose scope reaches Minecraft
 * (platform-wide or minecraft); an MC-scoped dev added later appears
 * automatically. The fuller admin/mod roster lives on the Hub (linked below).
 */
export default function TeamSection() {
  const { leadership } = useCommunity()
  const { t } = useLanguage()

  const core = useMemo(
    () =>
      leadership.filter(
        (m) => m.tier === 'core' && (m.scope === 'platform' || m.scope === 'minecraft'),
      ),
    [leadership],
  )

  if (core.length === 0) return null

  return (
    <section id="team" className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
      <SectionHeading
        eyebrow="Meet the team"
        title="The people behind the blocks"
        subtitle="Our shared Uliunai leadership keeps every server — Minecraft included — running and welcoming."
      />

      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {core.map((member) => (
          <TeamCard key={member.name} member={member} />
        ))}
      </div>

      <div className="mt-10 text-center">
        <a
          href="https://uliunai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-bold text-grass-deep underline-offset-4 transition-colors hover:text-grass-dark hover:underline"
        >
          {t('Meet the whole team on the Hub')} <ArrowUpRight size={16} />
        </a>
      </div>
    </section>
  )
}
